import { useContext, useState } from 'react';
import { styled } from 'styled-components';
import * as uuid from 'uuid';

import { Button } from '@vivaahealth/design-system';

import type { ReschedulingAppointmentCancelMessage } from '../../../../../../common/rescheduling';
import MessageContent from '../../../Message/messageContent';
import { ConversationContext } from '../../../../../../context/conversationContext';

import * as api from '../../../../../../api';
import { ConfigurationContext } from '../../../../../../context/configurationContext';

interface Props {
  message: ReschedulingAppointmentCancelMessage;
  showTextAnimation: boolean;
}

const AppointmentCancelling: React.FC<Props> = ({
  message,
  showTextAnimation,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const conversationContext = useContext(ConversationContext);
  const configurationContext = useContext(ConfigurationContext);
  const { appointment } = message.data;

  const cancelAppointment = async () => {
    if (!appointment.isCancelEnabled) return;
    if (isSubmitting) return;
    if (!conversationContext) return;
    if (!configurationContext?.id) return;
    setIsSubmitting(true);
    try {
      await api.client.Appointment.cancelAppointment(
        appointment.id,
        appointment.patientEhrId,
        configurationContext.id,
      );
      conversationContext.disableMessages();
      conversationContext.upsertLastMessage(
        {
          ...message,
          done: true,
          chat_message_id: uuid.v4(),
          answer:
            'Appointment canceled successfully. Is there anything else I can help with?',
          intentComponent:
            api.Assistant.IntentComponent
              .ReschedulingAppointmentActionConfirmed,
          data: { cancelled: appointment },
        },
        api.Assistant.IntentComponent.ReschedulingAppointmentActionConfirmed,
      );
      setIsSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MessageContent
        padTop={true}
        content={message.answer}
        isAgent={true}
        isLoading={false}
        showTextAnimation={showTextAnimation}
      />
      <CancelConfirmationWrapper
        $padBottom={submitted}
        data-test-id="cancel-reschedule-cancelling"
      >
        <WarningButton
          onClick={() => cancelAppointment()}
          disabled={(isSubmitting || message.disabled) ?? false}
        >
          Yes, cancel appointment
        </WarningButton>
        <InvertedButton
          onClick={() => {
            if (!conversationContext) return;
            conversationContext.removeLastMessage();
          }}
          disabled={(isSubmitting || message.disabled) ?? false}
        >
          No, don't cancel
        </InvertedButton>
      </CancelConfirmationWrapper>
    </>
  );
};

const WarningButton = styled(Button)`
  background-color: var(--color-state-danger) !important;
  margin-bottom: 0.5rem !important;

  &:disabled {
    color: var(--color-white) !important;
    opacity: 0.8;
  }
`;

const InvertedButton = styled(Button)`
  background-color: var(--color-white) !important;
  color: var(--font-color) !important;
  border: 1px solid var(--font-color) !important;
`;

const CancelConfirmationWrapper = styled.div.attrs<{ $padBottom: boolean }>(
  (props) => ({
    $padBottom: props.$padBottom ?? false,
  }),
)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => (props.$padBottom ? '64px' : '')};
`;

export default AppointmentCancelling;
